import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Box, Alert, Chip, Paper, Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApprovalDialog from '../../components/workflow/ApprovalDialog';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminTable from '../../components/shared/AdminTable';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

export default function WorkflowApprovals() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);

  useEffect(() => { loadApprovals(); }, []);

  async function loadApprovals() {
    setLoading(true);
    try {
      const data = await billingApi.get('/api/admin/workflows/approvals');
      setApprovals(data.approvals || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'definition_name',
      headerName: 'Workflow',
      width: 200,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.definition_name || 'Unknown Workflow'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
            {row.instance_id?.substring(0, 8)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      minWidth: 200,
      renderCell: (row) => row.reason || '—',
    },
    {
      field: 'started_by',
      headerName: 'Requested By',
      width: 140,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.8rem' }}>
          {row.started_by?.substring(0, 8) || '—'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Requested',
      width: 170,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 120,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (row) => (
        <Button size="small" variant="contained" onClick={() => setSelectedApproval(row)}>
          Review
        </Button>
      ),
    },
  ], []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pending Approvals</Typography>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${approvals.length} pending`}
          color={approvals.length > 0 ? 'warning' : 'default'}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <AdminPageSkeleton bare rows={5} />
      ) : approvals.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No pending approvals</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ height: 400 }}>
          <AdminTable
            rows={approvals}
            columns={columns}
            defaultSort={[{ field: 'created_at', direction: 'desc' }]}
            defaultPageSize={25}
          />
        </Paper>
      )}

      <ApprovalDialog
        open={!!selectedApproval}
        onClose={() => setSelectedApproval(null)}
        approval={selectedApproval}
        onDecided={() => loadApprovals()}
      />
    </Container>
  );
}
